import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Modal from "@mui/material/Modal";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@mui/material/MenuItem";
import { CircularProgress } from "@mui/material";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { setReporting } from "../redux/slices/reportingSlice";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 450,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  paddingTop: 2,
  paddingBottom: 2,
  paddingLeft: 3,
  paddingRight: 3,
  borderRadius: "10px",
};

const useStyles = makeStyles((theme) => ({
  root: {
    "& .MuiTextField-root": {
      margin: theme.spacing(1),
      width: "30ch",
      borderRadius: "15%",
    },
  },
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  paper: {
    position: "absolute",
    width: 300,
    backgroundColor: "#fff",
    boxShadow: "0 0 10px rgba(0, 0, 0, 0.5)",
    padding: 20,
    outline: "none",
    borderRadius: "2%",
    overflow: "hidden",
  },
  button: {
    textTransform: "lowercase",
  },
  labelContainer: {
    display: "flex",
    alignItems: "center",
    marginRight: "10px",
  },
  fieldContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
}));

export default function MyReportingForm({ selectedOption }) {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [success, setSuccessMessage] = useState();
  const emmsheaders = useSelector((state) => state.emmsheader);
  const [formData, setFormData] = useState({
    mailId: "",
    dataRange: "",
    startDate: "",
    scheduledDayOrDate: "",
    scheduledTime: "",
    customProperties: {},
    cronString: "",
  });
  const api = localStorage.getItem("api");
  const headers = useSelector((state) => state.header);
  const classes = useStyles();
  const dispatch = useDispatch();
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleModalClose = () => {
    setErrorModalOpen(false);
    setErrorMessage("");
    setOpen(false);
    setIsSubmitting(false);
  };
  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
  };
  const reporting = useSelector((state) => state.reporting);
  const [disabled, setDisabled] = useState(false);
  useEffect(() => {
    if (reporting?.reporting && reporting.reporting.length !== 0) {
      setDisabled(true);
    } else {
      setDisabled(false);
    }
  }, [reporting.reporting]);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (validateForm()) {
      setIsSubmitting(true);

      const [hour, minute] = formData.scheduledTime.split(":");

      let cronString = "";

      if (formData.dataRange === "60") {
        const dayOfMonth = new Date(formData.startDate).getDate();
        const month = new Date(formData.startDate).getMonth() + 1;

        let cronMonthPart;
        if (month % 2 === 0) {
          cronMonthPart = "2,4,6,8,10,12";
        } else {
          cronMonthPart = "1,3,5,7,9,11";
        }

        cronString = `${minute} ${hour} ${dayOfMonth} ${cronMonthPart} *`;
      } else if (isDayOrDateSelectionRequired) {
        if (formData.dataRange === "7") {
          const dayOfWeek = {
            Monday: 1,
            Tuesday: 2,
            Wednesday: 3,
            Thursday: 4,
            Friday: 5,
            Saturday: 6,
            Sunday: 0,
          };
          const cronDay = dayOfWeek[formData.scheduledDayOrDate];
          cronString = `${minute} ${hour} * * ${cronDay}`;
        }
      } else if (isDateRequired) {
        const dayOfMonth = new Date(formData.startDate).getDate();
        cronString = `${minute} ${hour} ${dayOfMonth} * *`;
      } else {
        cronString = `${minute} ${hour} * * *`;
      }

      formData.cronString = cronString;

      const requestBody = {
        path: "reporting",
        query: {},
        body: { ...formData },
        emmsId: `${selectedOption}`,
        requestType: "POST",
        headers: emmsheaders?.emmsheaders?.headers,
      };
      axios
        .post(api + `/emms/piggyback`, requestBody, headers)
        .then((response) => {
          setIsSubmitting(false);
          setErrorModalOpen(true);

          dispatch(setReporting([response.data]));
          setErrorMessage(`Reporting Schedule created successfully`);
          setSuccessMessage(true);

          setFormData({
            mailId: "",
            dataRange: "",
            startDate: isDayOrDateSelectionRequired ? "" : formData.startDate,
            scheduledDayOrDate: isDayOrDateSelectionRequired
              ? ""
              : formData.scheduledDayOrDate,
            scheduledTime: "",
            customProperties: {},
            cronString: "",
          });
        })
        .catch((error) => {
          setIsSubmitting(false);
          setErrorModalOpen(true);
          setErrorMessage(`Error creating report: ${error?.response?.data}`);
          setSuccessMessage(false);

          setFormData({
            mailId: "",
            dataRange: "",
            startDate: "",
            scheduledDayOrDate: "",
            scheduledTime: "",
            customProperties: {},
            cronString: "",
          });
        });
    }
  };

  const getLabelWithAsterisk = (labelText, isRequired) => (
    <div className={classes.labelContainer}>
      <label htmlFor={labelText}>{labelText}</label>
      {isRequired && <span style={{ color: "red" }}>*</span>}
    </div>
  );

  const dataRangeOptions = [
    { value: "1", label: "Last 1 Day (Daily)" },
    { value: "7", label: "Last 7 Days" },
    { value: "30", label: "Last 30 Days" },
    { value: "60", label: "Last 60 Days" },
  ];

  const dayOptions = [
    { value: "Monday", label: "Monday" },
    { value: "Tuesday", label: "Tuesday" },
    { value: "Wednesday", label: "Wednesday" },
    { value: "Thursday", label: "Thursday" },
    { value: "Friday", label: "Friday" },
    { value: "Saturday", label: "Saturday" },
    { value: "Sunday", label: "Sunday" },
  ];

  // Update the conditions for when to show day or date selections
  const isDayOrDateSelectionRequired = formData.dataRange === "7";
  const isDateRequired =
    formData.dataRange === "30" || formData.dataRange === "60";

  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!formData.mailId) {
      newErrors.mailId = "Mail ID is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.mailId)) {
      newErrors.mailId = "Enter a valid email address";
    }

    if (!formData.dataRange) {
      newErrors.dataRange = "Data Range is required";
    }

    if (isDayOrDateSelectionRequired && !formData.scheduledDayOrDate) {
      newErrors.scheduledDayOrDate = "Scheduled Day is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  return (
    <div>
      <Button
        variant="contained"
        color="primary"
        onClick={handleOpen}
        disabled={disabled}
        style={{
          textTransform: "none",
        }}
      >
        Add Report
      </Button>
      <Modal open={open} onClose={handleClose}>
        <Box sx={style}>
          <form
            className={classes.root}
            noValidate
            autoComplete="off"
            onSubmit={handleSubmit}
          >
            <h2>Report Scheduling</h2>

            <div className={classes.fieldContainer}>
              {getLabelWithAsterisk("Mail Address:", true)}
              <TextField
                required
                id="mailId"
                label="Mail ID"
                name="mailId"
                size="small"
                variant="outlined"
                value={formData.mailId}
                onChange={handleChange}
                error={!!errors.mailId}
                helperText={errors.mailId}
              />
            </div>

            <div className={classes.fieldContainer}>
              {getLabelWithAsterisk("Data Range:", true)}
              <TextField
                select
                required
                id="dataRange"
                name="dataRange"
                size="small"
                variant="outlined"
                value={formData.dataRange}
                onChange={handleChange}
                error={!!errors.dataRange}
                helperText={errors.dataRange}
              >
                {dataRangeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </div>

            {/* Conditionally render day or date selection only if required */}
            {isDayOrDateSelectionRequired && (
              <div className={classes.fieldContainer}>
                {getLabelWithAsterisk("Scheduled Day:", true)}
                <TextField
                  select
                  required
                  id="scheduledDayOrDate"
                  name="scheduledDayOrDate"
                  size="small"
                  variant="outlined"
                  value={formData.scheduledDayOrDate}
                  error={!!errors.scheduledDayOrDate}
                  helperText={errors.scheduledDayOrDate}
                  onChange={handleChange}
                >
                  {dayOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </div>
            )}

            {isDateRequired && (
              <div className={classes.fieldContainer}>
                {getLabelWithAsterisk("Start Date:", true)}
                <TextField
                  required
                  id="startDate"
                  name="startDate"
                  type="date"
                  size="small"
                  variant="outlined"
                  value={formData.startDate}
                  onChange={handleChange}
                  error={!!errors.startDate}
                  helperText={errors.startDate}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </div>
            )}

            <div className={classes.fieldContainer}>
              {getLabelWithAsterisk("Scheduled Time:", true)}
              <TextField
                required
                id="scheduledTime"
                name="scheduledTime"
                type="time"
                size="small"
                variant="outlined"
                value={formData.scheduledTime}
                onChange={handleChange}
                error={!!errors.scheduledTime}
                helperText={errors.scheduledTime}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </div>
            <div className={classes.fieldContainer}>
              {getLabelWithAsterisk("Cron String:", false)}
              <TextField
                id="cronStringUser"
                name="cronStringUser"
                variant="outlined"
                label="00 8 * * 5"
                size="small"
                fullWidth
                value=""
                onChange={handleChange}
                disabled
              />
            </div>

            <Button
              style={{
                marginTop: "10%",
                textTransform: "none",
                marginLeft: "75%",
              }}
              type="submit"
              variant="contained"
              color="primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? <CircularProgress size={24} /> : "Submit"}
            </Button>
          </form>
          <Modal
            open={errorModalOpen}
            onClose={handleModalClose}
            className={classes.modal}
          >
            <div className={classes.paper}>
              {success ? <h2>Congratulations</h2> : <h2>Error</h2>}
              <p>{errorMessage}</p>
              <Button
                style={{
                  textTransform: "none",

                  marginLeft: "75%",
                }}
                variant="contained"
                color="primary"
                onClick={handleModalClose}
              >
                Close
              </Button>
            </div>
          </Modal>
        </Box>
      </Modal>
    </div>
  );
}
